import { getStatusOption } from 'components/StatusSelect';
/**
 * Order the sections are rendered in. The server returns the groups in this
 * order already; the list is kept here so the frontend can sort the flattened
 * list of the preview the same way.
 *
 * @const SEARCH_ENTITY_ORDER
 * @since 1.0.0
 */
export const SEARCH_ENTITY_ORDER = [
    'CLIENT',
    'CONTACT',
    'DEAL',
    'ORDER',
    'TASK_LIST',
    'SALE_ITEM',
    'SALE_ITEM_OPTION',
    'SALE_ITEM_MANUFACTURING',
    'SALE_ITEM_PACKAGE',
    'SALE_ITEM_GROUP',
    'SALE_ITEM_VARIANT',
    'VOUCHER',
    'PROMO_CODE',
    'ENTITY_NOTE',
    'ENTITY_REMINDER',
    'DOCUMENTATION'
];
/**
 * Destination of a note or a reminder. They have no screen of their own, so
 * the parent record is opened instead.
 *
 * @function parentRoute
 * @since 1.0.0
 */
function parentRoute(item) {
    if (!item.parentUuid) {
        return null;
    }
    switch (item.parentEntity) {
        case 'CLIENT':
            return { name: 'client.update', data: { id: item.parentUuid } };
        case 'CONTACT':
            return { name: 'contact.update', data: { id: item.parentUuid } };
        case 'DEAL':
            return { name: 'deal.details', data: { id: item.parentUuid } };
        default:
            return null;
    }
}
/**
 * How each bucket is labelled and where it leads.
 *
 * The `:id` of every route is the uuid: Model.findIn(accountId, uuid) is what
 * the detail resolvers call, and search_index.uuid holds exactly that, so no
 * translation is needed.
 *
 * @const SEARCH_ENTITIES
 * @since 1.0.0
 */
export const SEARCH_ENTITIES = {
    CLIENT: {
        statusScope: 'CLIENT_CONTACT',
        labelKey: 'Clients',
        fallback: 'Companies',
        picto: 'fal building',
        route: (item) => ({ name: 'client.update', data: { id: item.uuid } })
    },
    CONTACT: {
        statusScope: 'CLIENT_CONTACT',
        labelKey: 'Contacts',
        fallback: 'Contacts',
        picto: 'fal user',
        route: (item) => ({ name: 'contact.update', data: { id: item.uuid } })
    },
    DEAL: {
        statusScope: 'DEAL',
        labelKey: 'Deals',
        fallback: 'Quotes',
        picto: 'fal file-invoice-dollar',
        route: (item) => ({ name: 'deal.details', data: { id: item.uuid } })
    },
    ORDER: {
        statusScope: 'ORDER',
        labelKey: 'Orders',
        fallback: 'Orders',
        picto: 'fal clipboard-list',
        route: (item) => ({ name: 'order.update', data: { id: item.uuid } })
    },
    TASK_LIST: {
        statusScope: 'TASK_LIST',
        labelKey: null,
        fallback: 'Function sheets',
        picto: 'fal list-check',
        route: (item) => ({
            name: 'taskAndAction.update',
            data: { id: item.uuid }
        }),
        /*
         * `order` and `task_list` share their uuid since the backfill of
         * 20261010100100, so the order screen is reachable from the same id. The
         * parent uuid is preferred anyway, to stay correct if that ever changes.
         */
        secondary: {
            permission: 'order.manage',
            label: () => t('Open the order'),
            route: (item) => {
                var _a;
                return ({
                    name: 'order.update',
                    data: { id: (_a = item.parentUuid) !== null && _a !== void 0 ? _a : item.uuid }
                });
            }
        }
    },
    SALE_ITEM: {
        labelKey: 'SaleItems',
        fallback: 'Products',
        picto: 'fal box',
        route: (item) => ({ name: 'saleItem.update', data: { id: item.uuid } })
    },
    SALE_ITEM_OPTION: {
        statusScope: null,
        labelKey: null,
        fallback: 'Options',
        picto: 'fal sliders',
        route: (item) => ({
            name: 'saleItemOption.update',
            data: { id: item.uuid }
        })
    },
    SALE_ITEM_MANUFACTURING: {
        statusScope: null,
        labelKey: 'ManufacturingItems',
        fallback: 'Tasks and actions',
        picto: 'fal screwdriver-wrench',
        // The screen lives at /products/elements, hence `manufacturing.update`
        // rather than a saleItemManufacturing route, which does not exist.
        route: (item) => ({
            name: 'manufacturing.update',
            data: { id: item.uuid }
        })
    },
    SALE_ITEM_PACKAGE: {
        labelKey: null,
        fallback: 'Packages',
        picto: 'fal boxes-packing',
        route: (item) => ({
            name: 'saleItemPackage.update',
            data: { id: item.uuid }
        })
    },
    SALE_ITEM_GROUP: {
        labelKey: null,
        fallback: 'Categories',
        picto: 'fal folder-tree',
        route: (item) => ({ name: 'saleItemGroup.update', data: { id: item.uuid } })
    },
    SALE_ITEM_VARIANT: {
        labelKey: null,
        fallback: 'Variants',
        picto: 'fal boxes-stacked',
        /*
         * Two route parameters: /products/variants/edit/:saleItemId/:id. Without
         * the parent uuid the URL would be broken, so the item is reported as not
         * navigable instead.
         */
        route: (item) => item.parentUuid
            ? {
                name: 'saleItemVariant.update',
                data: { saleItemId: item.parentUuid, id: item.uuid }
            }
            : null
    },
    VOUCHER: {
        labelKey: null,
        fallback: 'Vouchers',
        picto: 'fal ticket',
        route: (item) => ({ name: 'voucher.update', data: { id: item.uuid } })
    },
    PROMO_CODE: {
        labelKey: null,
        fallback: 'Promo codes',
        picto: 'fal tag',
        route: (item) => ({ name: 'promoCode.update', data: { id: item.uuid } })
    },
    ENTITY_NOTE: {
        labelKey: null,
        fallback: 'Notes',
        picto: 'fal note-sticky',
        route: parentRoute
    },
    ENTITY_REMINDER: {
        labelKey: null,
        fallback: 'Reminders',
        picto: 'fal bell',
        route: parentRoute
    },
    /*
     * documentation.view lives in PublicLayout, so inside the app an article is
     * opened in the documentation drawer rather than navigated to.
     */
    DOCUMENTATION: {
        labelKey: null,
        fallback: 'Documentation',
        picto: 'fal book',
        route: () => null
    }
};
/**
 * Resolves where an item leads.
 *
 * Returns null rather than throwing for an unknown entity: a bucket shipped by
 * the backend before the frontend knows about it makes the row unclickable, it
 * does not break the screen.
 *
 * @function searchEntityRoute
 * @since 1.0.0
 */
export function searchEntityRoute(item) {
    const descriptor = SEARCH_ENTITIES[item.entity];
    if (!descriptor) {
        return null;
    }
    return descriptor.route(item);
}
/**
 * Label of a bucket.
 *
 * Entity names that an account can rename (companies, contacts, quotes,
 * products, orders) must come from customLabels, NOT from t().
 *
 * @function searchEntityLabel
 * @since 1.0.0
 */
export function searchEntityLabel(entity, customLabels, language) {
    var _a;
    const descriptor = SEARCH_ENTITIES[entity];
    if (!descriptor) {
        return entity;
    }
    if (descriptor.labelKey && customLabels && language) {
        const custom = (_a = customLabels[descriptor.labelKey]) === null || _a === void 0 ? void 0 : _a[language];
        if (custom) {
            return custom;
        }
    }
    return t(descriptor.fallback);
}
/**
 * Flattens the groups into the single indexed list the keyboard walks.
 *
 * @function flattenGroups
 * @since 1.0.0
 */
export function flattenGroups(groups) {
    const ordered = [...groups].sort((left, right) => SEARCH_ENTITY_ORDER.indexOf(left.entity) -
        SEARCH_ENTITY_ORDER.indexOf(right.entity));
    return ordered.reduce((items, group) => { var _a; return items.concat((_a = group.items) !== null && _a !== void 0 ? _a : []); }, []);
}
/**
 * Resolves the label and colour of a row's status.
 *
 * Goes through getStatusOption so a quote marked Won reads "Won" in green
 * here exactly as it does in the quotes table, rather than showing the raw
 * database value.
 *
 * @function searchEntityStatus
 * @since 1.0.0
 */
export function searchEntityStatus(entity, status) {
    var _a, _b, _c;
    if (!status) {
        return null;
    }
    /*
     * DRAFT is set by the indexer rather than stored on the record, so it has
     * no entry in the status options.
     */
    if (status === 'DRAFT') {
        return { label: t('Draft'), color: 'light-gray' };
    }
    const scope = (_a = SEARCH_ENTITIES[entity]) === null || _a === void 0 ? void 0 : _a.statusScope;
    if (!scope) {
        return null;
    }
    const option = getStatusOption(scope, status);
    /*
     * Falls back to the raw value rather than to nothing, the same way
     * getClientContactStatusLabel does. Some statuses in the data have no entry
     * in the options (APPROVED on a deal, for one), and dropping the badge
     * would hide the state instead of merely leaving it untranslated.
     */
    return {
        label: String((_b = option === null || option === void 0 ? void 0 : option.label) !== null && _b !== void 0 ? _b : status),
        color: String((_c = option === null || option === void 0 ? void 0 : option.color) !== null && _c !== void 0 ? _c : 'light-gray')
    };
}
/**
 * Picto of a bucket.
 *
 * Shown once, next to the name of the section or group, rather than repeated
 * on every row: inside a section every row has the same type, so the icon
 * belongs to the heading.
 *
 * @function searchEntityPicto
 * @since 1.0.0
 */
export function searchEntityPicto(entity) {
    var _a;
    return (_a = SEARCH_ENTITIES[entity]) === null || _a === void 0 ? void 0 : _a.picto;
}
